import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';
import { Check, ArrowRight, Sparkles, Zap, Crown, Rocket, Calendar, Shield, Cpu, Smartphone, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PricingPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pricingTiers = [
    {
      id: 'essentials',
      title: 'Essentials',
      tagline: 'Best for early-stage startups & MVPs',
      priceRange: '$2,500 – $5,000',
      icon: <Sparkles className="h-8 w-8" />,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      features: [
        'Product discovery & technical planning',
        'Core SaaS application (single app)',
        'Authentication & user roles',
        'Basic dashboard & core workflows',
        'Responsive web app (desktop + mobile)',
        'Secure cloud setup (single environment)',
        'Basic integrations (Stripe, email, auth, etc.)',
        'Deployment & launch support'
      ],
      timeline: '4–8 weeks',
      idealFor: 'MVPs, internal tools, proof-of-concept SaaS products',
      cta: 'Start with Essentials',
      highlighted: false
    },
    {
      id: 'plus',
      title: 'Plus',
      tagline: 'Best for growing SaaS products & funded startups',
      priceRange: '$5,000 – $7,500',
      icon: <Zap className="h-8 w-8" />,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      includePrevious: 'Essentials',
      features: [
        'Advanced user roles & permissions',
        'Subscription billing & plans',
        'Scalable backend architecture',
        'Admin panel & analytics',
        'Third-party integrations (CRM, APIs, webhooks)',
        'Performance optimization',
        'Security best practices & monitoring',
        'Multi-environment setup (dev, staging, prod)',
        'Post-launch support window'
      ],
      timeline: '8–14 weeks',
      idealFor: 'Revenue-generating SaaS platforms, customer-facing products',
      cta: 'Choose Plus',
      highlighted: true,
      badge: 'MOST POPULAR'
    },
    {
      id: 'pro',
      title: 'PRO',
      tagline: 'Best for high-growth, enterprise, or complex SaaS platforms',
      priceRange: 'Custom Pricing',
      icon: <Crown className="h-8 w-8" />,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      includePrevious: 'Plus',
      features: [
        'Custom SaaS architecture & system design',
        'Multi-tenant infrastructure',
        'Advanced analytics & reporting',
        'High-availability & auto-scaling cloud setup',
        'Compliance readiness (SOC2, HIPAA, etc. if needed)',
        'Custom integrations & microservices',
        'Dedicated senior engineering team',
        'Long-term roadmap planning',
        'Priority support & ongoing development'
      ],
      timeline: '3–6+ months',
      idealFor: 'Enterprise SaaS, VC-backed startups, mission-critical platforms',
      cta: 'Go PRO',
      highlighted: false,
      badge: 'ENTERPRISE'
    }
  ];

  const addOns = [
    { icon: <Shield className="h-5 w-5" />, label: 'Ongoing SaaS maintenance & support (monthly)' },
    { icon: <Rocket className="h-5 w-5" />, label: 'Feature iterations & roadmap execution' },
    { icon: <Sparkles className="h-5 w-5" />, label: 'UI/UX design systems' },
    { icon: <Smartphone className="h-5 w-5" />, label: 'Mobile apps (iOS / Android)' },
    { icon: <Bot className="h-5 w-5" />, label: 'AI / automation features' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <SEO 
        title="Our Pricing - SaaS Development Pricing Built for Scale"
        description="Transparent SaaS development pricing from Fearless IT. From MVPs to enterprise platforms, find the right package for your business. Starting at $2,500."
        keywords="SaaS pricing, software development cost, MVP development pricing, enterprise SaaS development, custom software pricing"
        url="/our-pricing"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/dcbad53fc62f92be768b0e482a073261d8d3c3f6db590cf1019bc872adf551c0.png"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-12 pb-6 sm:pt-16 sm:pb-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4 sm:mb-6" data-testid="pricing-title">
              Our Pricing
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-4" style={{ background: 'linear-gradient(to right, #0066FF, #5B3DF5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              SaaS Development Pricing Built for Scale
            </p>
            <p className="text-base sm:text-lg text-slate-gray max-w-2xl mx-auto mb-8 sm:mb-10">
              Transparent pricing. No hidden fees. Choose the package that matches your growth stage and let's build something exceptional together.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto" data-testid="hero-start-project-btn">
                  <Rocket className="h-5 w-5 mr-2" />
                  Start Your Project
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-slate-gray text-slate-gray hover:border-fearless-blue hover:text-fearless-blue transition-all w-full sm:w-auto" data-testid="hero-talk-expert-btn">
                  Talk to an Expert
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-8 lg:items-stretch pt-6">
            {pricingTiers.map((tier, index) => (
              <div 
                key={tier.id}
                className={`relative rounded-2xl p-6 sm:p-8 transition-all duration-300 flex flex-col ${
                  tier.highlighted 
                    ? 'bg-gradient-to-b from-purple-50 to-white border-2 border-electric-indigo shadow-2xl lg:scale-105 z-10' 
                    : 'bg-white border-2 border-gray-200 hover:border-fearless-blue hover:shadow-xl'
                }`}
                data-testid={`pricing-card-${tier.id}`}
              >
                {/* Badge */}
                {tier.badge && (
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-bold text-white whitespace-nowrap shadow-lg ${
                    tier.highlighted ? 'bg-gradient-to-r from-fearless-blue to-electric-indigo' : 'bg-amber-500'
                  }`}>
                    {tier.badge}
                  </div>
                )}

                {/* Header */}
                <div className="mb-6">
                  <div className={`w-14 h-14 ${tier.iconBg} rounded-xl flex items-center justify-center mb-4 ${tier.iconColor}`}>
                    {tier.icon}
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-2">{tier.title}</h3>
                  <p className="text-sm text-slate-gray">{tier.tagline}</p>
                </div>

                {/* Price */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <p className="text-xs text-slate-gray uppercase tracking-wider mb-1">Starting Range</p>
                  <p className="text-2xl sm:text-3xl font-bold text-carbon-black">{tier.priceRange}</p>
                </div>

                {/* Includes Previous */}
                {tier.includePrevious && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-slate-gray">
                      <span className="font-semibold text-carbon-black">Includes everything in {tier.includePrevious}</span>, plus:
                    </p>
                  </div>
                )}

                {/* Features */}
                <div className="mb-6">
                  <p className="text-xs text-slate-gray uppercase tracking-wider mb-3">{tier.includePrevious ? '' : 'Includes:'}</p>
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-gray">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Timeline & Ideal For */}
                <div className="mb-6 space-y-3 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center text-sm">
                    <Calendar className="h-4 w-4 text-fearless-blue mr-2 flex-shrink-0" />
                    <span className="text-slate-gray"><span className="font-semibold text-carbon-black">Timeline:</span> {tier.timeline}</span>
                  </div>
                  <div className="flex items-start text-sm">
                    <Cpu className="h-4 w-4 text-fearless-blue mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-gray"><span className="font-semibold text-carbon-black">Ideal for:</span> {tier.idealFor}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <Link to="/contact">
                  <Button 
                    className={`w-full py-6 text-base font-semibold rounded-xl transition-all ${
                      tier.highlighted 
                        ? 'gradient-fearless text-white hover:opacity-90' 
                        : 'bg-white border-2 border-carbon-black text-carbon-black hover:bg-carbon-black hover:text-white'
                    }`}
                    data-testid={`pricing-cta-${tier.id}`}
                  >
                    {tier.cta}
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Optional Add-Ons Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-4">
              Optional Add-Ons
            </h2>
            <p className="text-base sm:text-lg text-slate-gray">
              Available for all tiers to extend and enhance your SaaS platform
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            {addOns.map((addon, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-xl p-5 sm:p-6 border border-gray-200 hover:border-fearless-blue hover:shadow-lg transition-all text-center"
              >
                <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center text-fearless-blue mb-4">
                  {addon.icon}
                </div>
                <p className="text-sm font-medium text-carbon-black">{addon.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-fearless-blue to-electric-indigo">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            Ready to Build Your SaaS Platform?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto">
            Let's discuss your vision and find the perfect development package for your business goals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-fearless-blue px-8 py-6 text-lg font-semibold rounded-xl hover:bg-gray-100 transition-all w-full sm:w-auto" data-testid="final-start-project-btn">
                <Rocket className="h-5 w-5 mr-2" />
                Start Your Project
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-white text-white hover:bg-white/10 transition-all w-full sm:w-auto" data-testid="final-schedule-btn">
                Schedule a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default PricingPage;
